import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Mail, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrdersTab = () => {
  const [query, setQuery] = useState('');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [reconcileId, setReconcileId] = useState('');
  const [reconciling, setReconciling] = useState(false);

  const search = async () => {
    if (!query.trim()) { toast.error('Enter a payment ID or customer email'); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/order-lookup`, { params: { q: query.trim() } });
      setOrders(res.data.orders);
      if (res.data.count === 0) toast.info('No matching completed orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const resend = async (paymentId) => {
    setResendingId(paymentId);
    try {
      const res = await axios.post(`${API}/admin/resend-access-email`, { payment_id: paymentId });
      if (res.data.status === 'sent') {
        toast.success(`Access email re-sent to ${res.data.email}`);
      } else {
        toast.error(res.data.detail || 'Email service did not accept the send');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend email');
    } finally {
      setResendingId(null);
    }
  };

  const reconcile = async () => {
    if (!reconcileId.trim()) { toast.error('Enter the PayPal payment ID to reconcile'); return; }
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/admin/reconcile-payment`, { payment_id: reconcileId.trim() });
      if (res.data.status === 'reconciled') {
        toast.success(`Access granted & email sent to ${res.data.email} (${res.data.tier})`);
        setReconcileId('');
      } else if (res.data.status === 'already_completed') {
        toast.info(`Already completed for ${res.data.email} — use Resend Email instead`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reconcile payment');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="orders-tab">
      <Card className="card-tech p-6">
        <h2 className="text-2xl font-bold text-green-400 mb-2">
          <Search className="w-6 h-6 inline mr-2" /> Order Lookup & Email Rescue
        </h2>
        <p className="text-green-300 text-sm mb-4">
          Find a completed order by payment ID or customer email, then re-send its access-code email
          if the buyer never received it.
        </p>
        <div className="flex items-stretch gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Payment ID (PAY-...) or customer@email.com"
            className="bg-black/50 border-green-500/30 text-green-300 h-11"
            data-testid="order-search-input"
          />
          <Button onClick={search} disabled={loading} className="btn-primary px-5 shrink-0" data-testid="order-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Search</>}
          </Button>
        </div>
      </Card>

      <Card className="card-tech p-6" data-testid="reconcile-card">
        <h2 className="text-xl font-bold text-yellow-400 mb-2">
          <Mail className="w-5 h-5 inline mr-2" /> Rescue a Captured Payment
        </h2>
        <p className="text-green-300 text-sm mb-4">
          If PayPal charged a buyer but they never got access (payment shows "failed"), paste the
          PayPal payment ID here to grant access and send their email. Use this for orders that don't
          appear in the lookup above.
        </p>
        <div className="flex items-stretch gap-2">
          <Input
            value={reconcileId}
            onChange={(e) => setReconcileId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && reconcile()}
            placeholder="PayPal payment ID (PAYID-... or PAY-...)"
            className="bg-black/50 border-yellow-500/30 text-green-300 h-11"
            data-testid="reconcile-input"
          />
          <Button onClick={reconcile} disabled={reconciling} className="btn-primary px-5 shrink-0" data-testid="reconcile-btn">
            {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Grant Access'}
          </Button>
        </div>
      </Card>

      {orders && orders.length > 0 && (
        <Card className="card-tech p-6" data-testid="order-results">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-green-500/70 text-left border-b border-green-500/20">
                  <th className="py-2 pr-4">Payment ID</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.payment_id} className="border-b border-green-500/10" data-testid={`order-row-${o.payment_id}`}>
                    <td className="py-3 pr-4 font-mono text-green-300 break-all max-w-[220px]">{o.payment_id}</td>
                    <td className="py-3 pr-4 text-green-300">{o.email || '—'}</td>
                    <td className="py-3 pr-4">
                      <span className={o.tier === 'pro' ? 'text-yellow-400 font-bold' : 'text-green-400'}>
                        {(o.tier || 'basic').toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-green-400">${(o.amount || 0).toFixed(2)}</td>
                    <td className="py-3 pr-4 text-green-500/70">{(o.timestamp || '').slice(0, 10)}</td>
                    <td className="py-3 pr-4">
                      <Button
                        onClick={() => resend(o.payment_id)}
                        disabled={resendingId === o.payment_id || !o.email}
                        className="btn-secondary px-3 py-2 text-xs"
                        data-testid={`resend-email-btn-${o.payment_id}`}
                      >
                        {resendingId === o.payment_id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <><Mail className="w-3 h-3 mr-1" /> Resend Email</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
